/**
 * News Gallery Configuration
 *
 * This file contains the configuration for the news gallery items.
 * Each news item includes locale-specific URLs for IT/EN versions.
 */

import type { FilterTagId, LocaleUrl, NewsGalleryConfig } from '@/types'
import { resolveAsset } from '@/utils/resolveAssets'

import { FILTER_COLORS } from './colors.config'

const imageAssets = import.meta.glob<{ default: string }>('@/assets/img/**/*.{jpg,png}', { eager: true })

/** Ordered list of filter tag IDs for display (as per CSV specification) */
const NEWS_GALLERY_TAG_ORDER: FilterTagId[] = ['all', 'invest', 'build', 'support', 'impact', 'grow', 'win']

/** Helper to generate a NewsTag from a FilterTagId */
const createNewsTag = (id: FilterTagId) => ({
  id,
  labelKey: `newsGallery.tags.${id}`,
  color: FILTER_COLORS[id]
})

/** Helper to create locale-specific URL object */
const localeUrl = (it: string, en: string): LocaleUrl => ({ it, en })

export const NEWS_GALLERY_CONFIG: NewsGalleryConfig = {
  tags: NEWS_GALLERY_TAG_ORDER.map(createNewsTag),
  items: [
    // ============================================
    // INVEST (BLU)
    // ============================================
    {
      id: 'invest-1',
      titleKey: 'newsGallery.items.invest1.title',
      descriptionKey: 'newsGallery.items.invest1.description',
      tag: 'invest',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/limpegno-di-beatrice-il-calcio-femminile',
        'https://www.unibocconi.it/en/news/beatrices-commitment-womens-soccer'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/blue/beatrice.jpg')
    },
    {
      id: 'invest-2',
      titleKey: 'newsGallery.items.invest2.title',
      descriptionKey: 'newsGallery.items.invest2.description',
      tag: 'invest',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/le-sliding-doors-di-frank-leenders',
        'https://www.unibocconi.it/en/news/frank-leenders-sliding-doors'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/blue/frank.jpg')
    },
    {
      id: 'invest-3',
      titleKey: 'newsGallery.items.invest3.title',
      descriptionKey: 'newsGallery.items.invest3.description',
      tag: 'invest',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/i-conti-della-ryder-cup-nelle-mani-di-una-bocconiana',
        'https://www.unibocconi.it/en/news/bocconian-balancing-books-ryder-cup'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/blue/maria.jpg')
    },
    {
      id: 'invest-4',
      titleKey: 'newsGallery.items.invest4.title',
      descriptionKey: 'newsGallery.items.invest4.description',
      tag: 'invest',
      url: 'https://www.olympics.com/ioc/media-rights-holders',
      variant: 'small',
      fontColor: 'light'
    },
    // ============================================
    // BUILD (NERO)
    // ============================================
    {
      id: 'build-1',
      titleKey: 'newsGallery.items.build1.title',
      descriptionKey: 'newsGallery.items.build1.description',
      tag: 'build',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/alla-guida-di-un-piccolo-paese-verso-un-evento-globale',
        'https://www.unibocconi.it/en/news/leading-small-town-global-event'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/black/bormio.jpg')
    },
    {
      id: 'build-2',
      titleKey: 'newsGallery.items.build2.title',
      descriptionKey: 'newsGallery.items.build2.description',
      tag: 'build',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/dallexpo-alle-olimpiadi-milano-alla-prova-della-maturita-globale',
        'https://www.unibocconi.it/en/news/expo-olympics-milan-faces-test-global-maturity'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/black/beppe.jpg')
    },
    // ============================================
    // SUPPORT (ROSSO)
    // ============================================
    {
      id: 'support-1',
      titleKey: 'newsGallery.items.support1.title',
      descriptionKey: 'newsGallery.items.support1.description',
      tag: 'support',
      url: 'https://thefsa.org.uk/about/',
      variant: 'small',
      fontColor: 'light'
    },
    // ============================================
    // IMPACT (BIANCO)
    // ============================================
    {
      id: 'impact-1',
      titleKey: 'newsGallery.items.impact1.title',
      descriptionKey: 'newsGallery.items.impact1.description',
      tag: 'impact',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/i-doppi-giochi-di-alessandro-andreoni',
        'https://www.unibocconi.it/en/news/alessandro-andreonis-double-games'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/white/alessandro.jpg')
    },
    {
      id: 'impact-2',
      titleKey: 'newsGallery.items.impact2.title',
      descriptionKey: 'newsGallery.items.impact2.description',
      tag: 'impact',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/bocconiani-5-cerchi',
        'https://www.unibocconi.it/en/news/five-ring-bocconians'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/white/bocconiani.jpg')
    },
    {
      id: 'impact-3',
      titleKey: 'newsGallery.items.impact3.title',
      descriptionKey: 'newsGallery.items.impact3.description',
      tag: 'impact',
      url: 'https://www.olympics.com/ioc/olympic-legacy/over-125-years-of-olympic-venues',
      variant: 'small',
      fontColor: 'dark'
    },
    {
      id: 'impact-4',
      titleKey: 'newsGallery.items.impact4.title',
      descriptionKey: 'newsGallery.items.impact4.description',
      tag: 'impact',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/la-scintilla-olimpica',
        'https://www.unibocconi.it/en/news/olympic-spark'
      ),
      variant: 'large',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/white/scintilla.jpg')
    },
    // ============================================
    // GROW (GIALLO)
    // ============================================
    {
      id: 'grow-1',
      titleKey: 'newsGallery.items.grow1.title',
      descriptionKey: 'newsGallery.items.grow1.description',
      tag: 'grow',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/con-il-basket-si-puo',
        'https://www.unibocconi.it/en/news/working-together-through-basketball'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/yellow/basket.jpg')
    },
    {
      id: 'grow-2',
      titleKey: 'newsGallery.items.grow2.title',
      descriptionKey: 'newsGallery.items.grow2.description',
      tag: 'grow',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/affrontare-lemancipazione-femminile-sul-campo',
        'https://www.unibocconi.it/en/news/tackling-female-empowerment-field'
      ),
      variant: 'large',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/yellow/emancipazione.jpg')
    },
    {
      id: 'grow-3',
      titleKey: 'newsGallery.items.grow3.title',
      descriptionKey: 'newsGallery.items.grow3.description',
      tag: 'grow',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/lo-sport-360-gradi',
        'https://www.unibocconi.it/en/news/sports-across-board'
      ),
      variant: 'large',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/yellow/360-gradi.jpg')
    },
    {
      id: 'grow-4',
      titleKey: 'newsGallery.items.grow4.title',
      descriptionKey: 'newsGallery.items.grow4.description',
      tag: 'grow',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/lo-sport-luguaglianza-di-genere',
        'https://www.unibocconi.it/en/news/sports-gender-equality'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/yellow/sport-e-uguaglianza.jpg')
    },
    // ============================================
    // WIN (VERDE)
    // ============================================
    {
      id: 'win-1',
      titleKey: 'newsGallery.items.win1.title',
      descriptionKey: 'newsGallery.items.win1.description',
      tag: 'win',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/un-playmaker-alla-guida-dello-sport-team',
        'https://www.unibocconi.it/en/news/playmaker-lead-bocconi-sport-team'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/green/playmaker.jpg')
    },
    {
      id: 'win-2',
      titleKey: 'newsGallery.items.win2.title',
      descriptionKey: 'newsGallery.items.win2.description',
      tag: 'win',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/il-bivio-di-nicolo-carucci',
        'https://www.unibocconi.it/en/news/olympic-rower-nicolo-carucci-crossroads'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/green/carucci.jpg')
    },
    {
      id: 'win-3',
      titleKey: 'newsGallery.items.win3.title',
      descriptionKey: 'newsGallery.items.win3.description',
      tag: 'win',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/studio-e-sport-si-puo-passo-di-marcia',
        'https://www.unibocconi.it/en/news/study-and-sports-you-can-stride'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/green/studio-e-sport.jpg')
    },
    {
      id: 'win-4',
      titleKey: 'newsGallery.items.win4.title',
      descriptionKey: 'newsGallery.items.win4.description',
      tag: 'win',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/il-sogno-realizzato-di-federico',
        'https://www.unibocconi.it/en/news/federicos-dream-come-true'
      ),
      variant: 'medium',
      fontColor: 'dark',
      imageUrl: resolveAsset(imageAssets, 'news/green/federico.jpg')
    },
    {
      id: 'win-5',
      titleKey: 'newsGallery.items.win5.title',
      descriptionKey: 'newsGallery.items.win5.description',
      tag: 'win',
      url: localeUrl(
        'https://www.unibocconi.it/it/news/sarah-litrico-una-giocatrice-inter-nazionale',
        'https://www.unibocconi.it/en/news/sarah-litrico-international-player'
      ),
      variant: 'large',
      fontColor: 'light',
      imageUrl: resolveAsset(imageAssets, 'news/green/nazionale.jpg')
    }
  ]
}
